"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { type MessageStatus, MessageStatusIcon } from "./message-status";
import { UnsupportedMessageBubble } from "./unsupported-message-bubble";
import "@/components/ui/whatsapp/styles/whatsapp.css";

interface VideoBubbleProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "incoming" | "outgoing";
  src: string;
  caption?: string;
  /** e.g. "0:42" */
  duration?: string;
  timestamp?: string;
  status?: MessageStatus;
  showTail?: boolean;
  onExpand?: () => void;
}

const VideoBubble = React.forwardRef<HTMLDivElement, VideoBubbleProps>(
  (
    {
      className,
      variant = "incoming",
      src,
      caption,
      duration,
      timestamp,
      status,
      showTail = false,
      onExpand,
      ...props
    },
    ref
  ) => {
    const isOutgoing = variant === "outgoing";
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [playing, setPlaying] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);

    const togglePlay = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play();
        setPlaying(true);
      } else {
        video.pause();
        setPlaying(false);
      }
    };

    const handleEnded = () => setPlaying(false);

    if (hasError) {
      return (
        <div
          className={cn(
            "flex w-full",
            isOutgoing ? "justify-end" : "justify-start",
            showTail ? "mb-[6px]" : "mb-[2px]",
            className
          )}
          ref={ref}
          {...props}
        >
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="block no-underline cursor-pointer max-w-[420px] w-full"
          >
            <UnsupportedMessageBubble
              variant={variant}
              title="Vídeo indisponível"
              description="Vídeo indisponível — abrir original"
              timestamp={timestamp}
              status={status}
              showTail={showTail}
            />
          </a>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex w-full",
          isOutgoing ? "justify-end" : "justify-start",
          showTail ? "mb-[6px]" : "mb-[2px]",
          className
        )}
        {...props}
        ref={ref}
      >
        <div
          className={cn(
            "font-wa relative w-full max-w-[420px] overflow-hidden rounded-lg group/videobubble",
            showTail && (isOutgoing ? "rounded-br-[3px]" : "rounded-bl-[3px]")
          )}
        >
          {/* Video */}
          <div 
            className="relative cursor-pointer bg-wa-gray-800" 
            onClick={togglePlay}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onExpand?.();
            }}
          >
            {!loaded && (
              <div className="flex h-[240px] w-full max-w-[420px] items-center justify-center">
                <div className="animate-pulse bg-wa-gray-700 absolute inset-0" />
              </div>
            )}

            <video
              ref={videoRef}
              src={src}
              onLoadedData={() => setLoaded(true)}
              onError={() => setHasError(true)}
              onEnded={handleEnded}
              playsInline
              preload="metadata"
              className={cn(
                "w-full max-h-[400px] object-cover transition-opacity duration-300",
                loaded ? "opacity-100" : "opacity-0"
              )}
            />

            {/* Expand Fullscreen Button */}
            {loaded && onExpand && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand();
                }}
                className="absolute top-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white opacity-80 transition-all hover:bg-black/90 hover:opacity-100 hover:scale-105 active:scale-95 shadow-md"
                title="Abrir em tela cheia"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              </button>
            )}

            {/* Play button overlay */}
            {loaded && !playing && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 shadow-lg backdrop-blur-[2px]">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="white" className="ml-1">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}

            {/* Duration badge */}
            {duration && loaded && !playing && (
              <span className="absolute bottom-[6px] left-[6px] rounded bg-black/50 px-[5px] py-[2px] text-[11px] font-medium text-white">
                {duration}
              </span>
            )}

            {/* Timestamp overlay (no caption) */}
            {!caption && loaded && (
              <div className="absolute bottom-[6px] right-[8px] flex items-center gap-[3px] rounded-full bg-black/40 px-[6px] py-[2px]">
                {timestamp && <span className="text-[11px] leading-[15px] text-white">{timestamp}</span>}
                {isOutgoing && status && (
                  <span className="text-white"><MessageStatusIcon status={status} /></span>
                )}
              </div>
            )}
          </div>

          {/* Caption */}
          {caption && (
            <div className={cn("px-[9px] pb-[7px] pt-[4px]", isOutgoing ? "bg-wa-bubble-outgoing" : "bg-wa-bubble-incoming")}>
              <p className="text-[14.2px] leading-[19px] text-wa-text-primary break-words [overflow-wrap:anywhere] min-w-0">{caption}</p>
              <div className="mt-[2px] flex items-center justify-end gap-[3px]">
                {timestamp && <span className="text-[11px] leading-[15px] text-wa-bubble-meta">{timestamp}</span>}
                {isOutgoing && status && <MessageStatusIcon status={status} />}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
VideoBubble.displayName = "VideoBubble";

export { VideoBubble, type VideoBubbleProps };
